import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cancelSession } from '../api/cover'
import { expectANote } from '../api/fanOut'

/**
 * Calling one session off: the count, the two boxes, and the confirm (D-889).
 *
 * **Extracted from `CallItOff` so two screens share one implementation rather
 * than two that agree today** (A-17, D-199). The cover dialog reaches it after a
 * sub search has failed; the register reaches it because the dancer cannot make
 * it. Those are different sentences about the same irreversible act, so the
 * *wording* is the caller's and everything else lives here.
 *
 * **What a second copy would lose first**, in the order it would go missing:
 *
 * | | |
 * |---|---|
 * | `expectANote()` | Since D-869 the fan-out runs *after* the response and this dialog closes at once, so the bell is the only thing that ever says the families were told. **Nothing fails without it** |
 * | The preview gate | She must see *"this will tell 14 families"* before she can send. A failed preview must never read as *nobody will be told* (D-209) |
 * | The two boxes | The reason is required and Tonya's; the note is optional and the families' (D-659). One field with two audiences is one somebody writes the wrong sentence into |
 *
 * `CoverActions.test.tsx` was written against the original before this moved,
 * precisely so the move had a net: ten mutations redden it.
 */
export function CallOffConfirm({
  occurrenceId,
  trigger,
  triggerClassName,
  reasonLabel,
  reasonPlaceholder,
  reasons,
  onDone,
}: {
  occurrenceId: string

  /**
   * What the closed control says, and it is the caller's because the two
   * callers are answering different questions. *"Nobody can take it"* is false
   * on a register where the dancer is the one who cannot come.
   */
  trigger: ReactNode
  triggerClassName: string

  /** Her prompt for the studio's reason, and the hint inside the box. */
  reasonLabel: string
  reasonPlaceholder?: string

  /**
   * A short list to pick from instead of typing (D-890).
   *
   * **The server requires a reason**, so this is not a convenience — it is what
   * fills a mandatory field on a phone, and it yields sentences Tonya can scan
   * rather than forty spellings of the same one. The options are ours to write:
   * they are inside the signed-in app, so a literal and never a `ContentBlock`
   * (A-16 as narrowed by D-678).
   *
   * **The value stored is the bare option text**, matching the canonical strings
   * a closure already writes. The `Cancelled: ` prefix is added at render by the
   * one screen that shows it, so putting it here would read *"Cancelled:
   * Cancelled: …"*.
   *
   * **There is always a way out of the list.** Without one, a reason nobody
   * anticipated forces a wrong pick or no cancel at all — and the wrong pick is
   * worse, because it is the sentence Tonya reads.
   */
  reasons?: readonly string[]

  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [familyNote, setFamilyNote] = useState('')
  const [typingOther, setTypingOther] = useState(false)
  const queryClient = useQueryClient()

  // The list is only ever offered where the caller gave one; everything else
  // keeps the free-text box it has always had.
  const typing = reasons === undefined || typingOther

  /*
   * **How many families are about to hear, asked before she commits** (D-356).
   *
   * The endpoint previews on anything that is not literally `apply`, so opening
   * the confirm step costs a harmless request and buys her the number. She acts
   * on *"this tells 14 families"*, not on a bare button — and a solo where the
   * answer is one reads completely differently from a Wednesday combo where it
   * is sixty.
   */
  const preview = useQuery({
    queryKey: ['cover', 'cancel-preview', occurrenceId],
    queryFn: () => cancelSession(occurrenceId, 'preview', false),
    enabled: open,
    staleTime: 0,
  })

  const run = useMutation({
    mutationFn: () => cancelSession(occurrenceId, reason.trim(), true, familyNote.trim() || null),
    onSuccess: () => {
      setOpen(false)
      setReason('')
      setFamilyNote('')

      /*
       * <b>A note follows, so the bell has to look for it</b> (D-869).
       *
       * ⚠ This dialog is the one that loses most by the change and it is the
       * one that shows nothing: it discards the response, so before the
       * hand-off the *Saving* spinner itself was the signal that sixty
       * households had been told. Now it closes at once — which is what was
       * asked for — and the bell is the only thing left to say it happened.
       * Without this, the net effect on the app's single most far-reaching
       * action would have been *faster, and silent*.
       */
      expectANote()
      queryClient.invalidateQueries({ queryKey: ['notifications'] })

      onDone()
    },
  })

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={triggerClassName}>
        {trigger}
      </button>
    )
  }

  return (
    <div className="mt-2 w-full rounded-xl border border-line-2 bg-surface-2 p-2.5">
      <fieldset className="flex flex-col gap-1">
        <legend className="text-[12px] font-semibold text-ink-2">{reasonLabel}</legend>

        {reasons?.map((option) => (
          <label key={option} className="flex min-h-[32px] items-center gap-2 text-[13px] text-ink">
            <input
              type="radio"
              name={`why-${occurrenceId}`}
              checked={!typingOther && reason === option}
              onChange={() => {
                setTypingOther(false)
                setReason(option)
              }}
            />
            {option}
          </label>
        ))}

        {reasons !== undefined && (
          <label className="flex min-h-[32px] items-center gap-2 text-[13px] text-ink">
            <input
              type="radio"
              name={`why-${occurrenceId}`}
              checked={typingOther}
              onChange={() => {
                setTypingOther(true)
                setReason('')
              }}
            />
            Something else
          </label>
        )}

        {typing && (
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder={reasonPlaceholder}
            aria-label={reasonLabel}
            className="min-h-[36px] rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px]"
          />
        )}
      </fieldset>

      {/*
        The families' half, and it names its reader rather than leaving her to
        guess (D-659). Optional on purpose: left empty, the email goes out
        exactly as it always has.
      */}
      <label className="mt-2 flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-2">
          Anything to tell the families? Goes in their email, in your words.
        </span>
        <input
          value={familyNote}
          onChange={(e) => setFamilyNote(e.target.value)}
          maxLength={500}
          placeholder="Optional"
          className="min-h-[36px] rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px]"
        />
      </label>

      {/*
        The number before the send. A failed preview never renders as "nobody
        will be told" (D-209) — that is the one sentence she must not act on.
      */}
      {preview.isSuccess && (
        <p className="mt-2 text-[12px] font-semibold text-ink-2">
          {preview.data.families === 0
            ? 'Nobody is enrolled, so no one will be told.'
            : preview.data.families === 1
              ? 'This will tell 1 family the class is off.'
              : `This will tell ${preview.data.families} families the class is off.`}
        </p>
      )}

      {preview.isError && (
        <p className="mt-2 text-[12px] text-crit">
          We could not work out who would be told. Try again before calling it off.
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={reason.trim().length === 0 || run.isPending || !preview.isSuccess}
          onClick={() => run.mutate()}
          className="min-h-[36px] rounded-lg bg-crit px-3 py-1.5 text-[13px] font-semibold text-on-crit disabled:opacity-50"
        >
          {run.isPending ? 'Calling it off...' : 'Call it off and tell the families'}
        </button>

        <button
          type="button"
          onClick={() => setOpen(false)}
          className="min-h-[36px] px-2 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          Keep it on
        </button>
      </div>

      {run.isError && (
        <p role="alert" className="mt-1.5 text-[12px] text-crit">
          {(run.error as Error).message}
        </p>
      )}
    </div>
  )
}
